import {
  CategorySection,
  CategoryViewResult,
  CategoryViewResultTypenames,
  Maybe,
  VendorItem,
} from '@varo/graphql-schema-types';
import { mapVendorItemToLocalOfferLocation } from 'features/rewards/helpers/mapVendorItemToLocalOfferLocation';
import { useMemo } from 'react';
import { CombinedError, RequestPolicy } from 'urql';

import useNearByOffers from './useNearByOffers';

export interface UseLocalVendorsOptions {
  categoryId: string;
  latitude?: number;
  longitude?: number;
  pause?: boolean;
}

export interface UseLocalVendorsResult {
  localVendors: VendorItem[];
  localSections: CategorySection[];
  loading: boolean;
  error: CombinedError | undefined;
  reload: (options?: { requestPolicy?: RequestPolicy }) => void;
}

export const getLocalVendorsFromOffers = (
  offers: Maybe<CategoryViewResult> | undefined,
): VendorItem[] => {
  const result: VendorItem[] = [];

  if (offers?.__typename === CategoryViewResultTypenames.CategoryViewSuccess) {
    const seenIds = new Set<string>();

    for (const section of offers.sections ?? []) {
      for (const vendor of section.vendors ?? []) {
        const id = vendor?.id;
        const isLocal = !!vendor?.address;
        const hasCoords = !!mapVendorItemToLocalOfferLocation(vendor);
        if (!id || seenIds.has(id) || !isLocal || !hasCoords) {
          continue;
        }
        seenIds.add(id);
        result.push(vendor);
      }
    }
  }

  return result;
};

export const getLocalSectionsFromOffers = (
  offers: Maybe<CategoryViewResult> | undefined,
): CategorySection[] => {
  if (offers?.__typename !== CategoryViewResultTypenames.CategoryViewSuccess) {
    return [];
  }
  const seenIds = new Set<string>();
  const result: CategorySection[] = [];

  for (const section of offers.sections ?? []) {
    if (!section) {
      continue;
    }
    const filteredVendors: VendorItem[] = [];
    for (const vendor of section.vendors ?? []) {
      const id = vendor?.id;
      const isLocal = !!vendor?.address;
      const hasCoords = !!mapVendorItemToLocalOfferLocation(vendor);
      if (!id || seenIds.has(id) || !isLocal || !hasCoords) {
        continue;
      }
      seenIds.add(id);
      filteredVendors.push(vendor);
    }
    if (filteredVendors.length > 0) {
      result.push({ ...section, vendors: filteredVendors });
    }
  }

  return result;
};

const useLocalVendors = (
  options: UseLocalVendorsOptions,
): UseLocalVendorsResult => {
  const { offers, loading, error, reload } = useNearByOffers(options);

  const localVendors = useMemo(
    () =>
      offers?.__typename === CategoryViewResultTypenames.CategoryViewSuccess
        ? getLocalVendorsFromOffers(offers)
        : [],
    [offers],
  );

  const localSections = useMemo(
    () => getLocalSectionsFromOffers(offers),
    [offers],
  );

  return { localVendors, localSections, loading, error, reload };
};

export default useLocalVendors;
